(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!root.RealmSizeGuideLayout) {
    root.RealmSizeGuideLayout = api;
    const start = () => { api.runtime = api.mount(root.document, root); };
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  function scrollState(width, extent, offset, direction = 'ltr') {
    const visible = Math.max(0, Number(width) || 0), total = Math.max(visible, Number(extent) || 0);
    const overflow = total - visible > 1;
    const position = direction === 'rtl' ? Math.abs(Number(offset) || 0) : Math.max(0, Number(offset) || 0);
    return { overflow, start: !overflow || position <= 1, end: !overflow || position >= total - visible - 1 };
  }
  function authoredUnit(text) {
    const source = String(text || '').toLowerCase();
    const cm = /\b(?:metric|cm|centimet(?:er|re)s?)\b/.test(source);
    const inches = /^\s*in\s*$|\b(?:imperial|inches|inch)\b|\(\s*in\s*\)|\bcm\s*(?:\/|or|\|)\s*in\b|\bin\s*(?:\/|or|\|)\s*cm\b/.test(source);
    return cm === inches ? null : cm ? 'cm' : 'in';
  }
  const sizeVisibility = new WeakMap();
  function sizeToken(value) {
    const text = String(value ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
    const compact = text.replace(/[\s_-]/g, '');
    const aliases = { XXS: '2XS', XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', EXTRASMALL: 'XS', EXTRALARGE: 'XL', ONESIZE: 'ONE SIZE', OS: 'ONE SIZE' };
    return aliases[compact] || text;
  }
  function productSizes(guide) {
    try {
      const values = JSON.parse(guide.dataset.guideProductSizes || '[]');
      if (!Array.isArray(values) || values.length > 250 || values.some(value => typeof value !== 'string' || !value.trim() || value.length > 128)) return new Set();
      return new Set(values.map(sizeToken));
    } catch (_) { return new Set(); }
  }
  function filterTableSizes(table, allowed) {
    for (const node of table.querySelectorAll('[data-guide-size-filtered]')) {
      node.hidden = sizeVisibility.get(node) === true;
      node.removeAttribute('data-guide-size-filtered');
    }
    if (!allowed?.size) return null;
    const rows = Array.from(table.rows || []);
    if (!rows.length || rows.some(row => Array.from(row.cells).some(cell => cell.colSpan > 1 || cell.rowSpan > 1 || cell.querySelector('table')))) return null;
    const standard = token => /^(?:[2-9]XS|XS|S|M|L|XL|[2-9]XL|ONE SIZE)$/.test(token);
    const number = token => /^\d{1,3}(?:\.\d)?$/.test(token);
    const sizeHeading = text => /^(?:SIZE|SIZES|GARMENT SIZE|LABEL SIZE)(?:\s*\((?:CM|IN)\))?$/.test(sizeToken(text));
    const first = rows[0], header = Array.from(first.cells), explicit = sizeHeading(header[0]?.textContent);
    const start = standard(sizeToken(header[0]?.textContent)) || allowed.has(sizeToken(header[0]?.textContent)) ? 0 : 1;
    const columns = header.slice(start).map((cell, offset) => ({ cell, index: start + offset, token: sizeToken(cell.textContent) }));
    const columnAxis = columns.length >= 2 && columns.every(item => standard(item.token) || allowed.has(item.token) || explicit && number(item.token)) && columns.some(item => allowed.has(item.token)) && rows.every(row => row.cells.length === header.length);
    function visible(node, show) {
      if (!sizeVisibility.has(node)) sizeVisibility.set(node, node.hidden);
      node.hidden = sizeVisibility.get(node) === true || !show;
      node.dataset.guideSizeFiltered = String(!show);
    }
    if (columnAxis) {
      for (const item of columns) for (const row of rows) visible(row.cells[item.index], allowed.has(item.token));
      const colgroup = Array.from(table.querySelectorAll(':scope > colgroup > col'));
      if (colgroup.length === header.length && colgroup.every(col => col.span === 1)) for (const item of columns) visible(colgroup[item.index], allowed.has(item.token));
      return { axis: 'columns', visibleColumns: start + columns.filter(item => allowed.has(item.token)).length };
    }
    const candidates = rows.filter(row => row !== first || !explicit).filter(row => !row.closest('thead')).map(row => ({ row, token: sizeToken(row.cells[0]?.textContent) })).filter(item => standard(item.token) || allowed.has(item.token) || explicit && number(item.token));
    const rowAxis = candidates.some(item => allowed.has(item.token)) && (explicit || candidates.length >= 2 && candidates.every(item => standard(item.token)));
    if (!rowAxis) return null;
    for (const item of candidates) visible(item.row, allowed.has(item.token));
    return { axis: 'rows', visibleColumns: header.length };
  }
  function filterGuideSizes(guide) {
    const allowed = productSizes(guide);
    for (const table of guide.querySelectorAll('table')) filterTableSizes(table, allowed);
  }
  function preparePage(content, document, window) {
    const guide = content.closest('#RealmSizeGuide');
    if (!guide) return;
    // Read the selected page's actual DOM. Never cache a page body by product,
    // template or handle; a reassignment/section refresh uses the new source.
    let unit = null, heading = guide.querySelector('#RealmGuideHeading');
    const callerTitle = String(content.dataset.guideProductTitle || '').trim();
    const guideLabel = String(content.dataset.guideLabel || heading?.textContent || 'Size guide').trim();
    const callerLabel = callerTitle ? `${callerTitle} — ${guideLabel}` : '';
    const sourceTitle = String(content.dataset.guideSourceTitle || '').trim().replace(/\s+/g, ' ').toLowerCase();
    let callerHeadingUpdated = false;
    const tables = [];
    for (const item of content.querySelectorAll('h1,h2,h3,h4,h5,h6,table')) {
      if (item.tagName !== 'TABLE') {
        const next = item.dataset.guideCallerHeading === 'true' ? null : authoredUnit(item.textContent);
        const sourceHeading = item.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
        if (!next && callerLabel && !callerHeadingUpdated && (item.dataset.guideCallerHeading === 'true' || sourceTitle && sourceHeading === sourceTitle)) {
          item.dataset.guideIdentityHeading = 'true';
          item.dataset.guideCallerHeading = 'true';
          callerHeadingUpdated = true;
        }
        if (next) { unit = next; item.dataset.guideAuthoredUnit = next; }
        else { unit = null; heading = item; delete item.dataset.guideAuthoredUnit; item.hidden = item.dataset.guideIdentityHeading === 'true'; }
        continue;
      }
      const declared = ['cm', 'in'].includes(item.dataset.guideUnit) ? item.dataset.guideUnit : authoredUnit(item.querySelector('caption')?.textContent);
      const sourceUnit = declared || unit;
      const rows = Array.from(item.rows || item.querySelectorAll('tr'));
      const columns = Math.max(1, ...rows.map(row => Array.from(row.cells || row.querySelectorAll('th,td')).filter(cell => !cell.hidden).length));
      let numeric = false;
      rows.forEach((row, index) => {
        const cells = Array.from(row.cells || row.querySelectorAll('th,td'));
        cells.forEach((cell, column) => {
          if (cell.tagName === 'TH') cell.setAttribute('scope', index === 0 ? 'col' : column === 0 ? 'row' : 'col');
          else if (index > 0 && column === 0) cell.setAttribute('role', 'rowheader');
          if (index > 0 && column > 0 && /^\d+(?:\.\d+)?$/.test(cell.textContent.trim()) && Number(cell.textContent.trim()) > 0) numeric = true;
        });
      });
      item.dataset.guideTable = '';
      item.hidden = false;
      if (sourceUnit) item.dataset.guideAuthoredUnit = sourceUnit;
      else delete item.dataset.guideAuthoredUnit;
      item.dataset.guideAuthoredMeasurements = String(numeric);
      const chartLabel = heading?.textContent.trim() || guideLabel;
      const ownedLabel = callerLabel && chartLabel !== callerLabel ? `${callerTitle} — ${chartLabel}` : callerLabel || chartLabel;
      item.setAttribute('aria-label', `${ownedLabel}${sourceUnit ? ` (${sourceUnit.toUpperCase()})` : ''}`);
      item.style.setProperty('--realm-guide-source-table-width', `${Math.max(360, 104 * columns)}px`);
      let scroller = item.closest('.realm-table-scroll');
      if (!scroller) {
        scroller = document.createElement('div'); scroller.className = 'realm-table-scroll';
        item.before(scroller); scroller.append(item);
      }
      scroller.setAttribute('role', 'region'); scroller.setAttribute('tabindex', '0');
      scroller.hidden = false;
      scroller.setAttribute('aria-label', item.getAttribute('aria-label'));
      if (sourceUnit) scroller.dataset.guideAuthoredUnit = sourceUnit;
      else delete scroller.dataset.guideAuthoredUnit;
      tables.push({ table: item, unit: sourceUnit, numeric, group: heading?.textContent.trim() || '', heading });
    }
    const cm = tables.some(table => table.unit === 'cm' && table.numeric);
    const inches = tables.some(table => table.unit === 'in' && table.numeric);
    const groups = [...new Set(tables.map(table => table.group))];
    const paired = groups.every(group => tables.some(table => table.group === group && table.unit === 'cm') && tables.some(table => table.group === group && table.unit === 'in'));
    const known = tables.length > 0 && paired && tables.every(table => table.unit && table.numeric);
    if (groups.length === 1) {
      const groupHeading = tables[0]?.heading;
      if (groupHeading && groupHeading !== guide.querySelector('#RealmGuideHeading') && /(?:^|\s)size\s+(?:guide|chart)\s*$/i.test(groupHeading.textContent.trim()) && !groupHeading.querySelector('img,svg')) {
        groupHeading.dataset.guideIdentityHeading = 'true';
        groupHeading.hidden = true;
      }
    }
    guide.dataset.guideSourceMode = 'authored-page';
    guide.dataset.guideAuthoredUnitsReady = String(known && cm && inches);
    guide.dataset.guideMeasurementsApproved = String(known && cm && inches);
    // The existing guide controller owns unit clicks/focus. It only switches
    // the authored tables here; the merchant's actual values remain unchanged.
    window.RealmProductDetails?.mountGuide(guide);
  }
  function mount(document, window) {
    const entries = new Map();
    function inspect(scroller) {
      const entry = entries.get(scroller);
      if (!entry || scroller.isConnected === false) return;
      const state = scrollState(scroller.clientWidth, scroller.scrollWidth, scroller.scrollLeft, window.getComputedStyle?.(scroller)?.direction);
      scroller.dataset.guideOverflow = String(state.overflow);
      scroller.dataset.guideScrollStart = String(state.start);
      scroller.dataset.guideScrollEnd = String(state.end);
      if (entry.cue) entry.cue.hidden = !state.overflow;
    }
    function register(scope = document) {
      const guides = scope.matches?.('#RealmSizeGuide') ? [scope] : scope.querySelectorAll?.('#RealmSizeGuide') || [];
      for (const guide of guides) filterGuideSizes(guide);
      const pages = scope.querySelectorAll?.('#RealmSizeGuide [data-realm-guide-page-content]') || [];
      for (const content of pages) preparePage(content, document, window);
      const scrollers = scope.querySelectorAll?.('#RealmSizeGuide .realm-table-scroll') || [];
      for (const scroller of scrollers) {
        if (entries.has(scroller)) { inspect(scroller); continue; }
        const cue = document.getElementById?.(scroller.getAttribute('aria-describedby'));
        const update = () => inspect(scroller);
        const observer = window.ResizeObserver ? new window.ResizeObserver(update) : null;
        entries.set(scroller, { cue, update, observer });
        scroller.addEventListener('scroll', update, { passive: true });
        observer?.observe(scroller);
        const table = scroller.querySelector('table');
        if (table) observer?.observe(table);
        inspect(scroller);
      }
    }
    function unregister(scope) {
      for (const [scroller, entry] of entries) {
        if (!scope || scope.contains?.(scroller) || scroller.isConnected === false) {
          entry.observer?.disconnect();
          scroller.removeEventListener('scroll', entry.update);
          entries.delete(scroller);
        }
      }
    }
    const refresh = () => { register(); for (const scroller of entries.keys()) inspect(scroller); };
    const load = event => register(event.target);
    const unload = event => unregister(event.target);
    document.addEventListener('shopify:section:load', load);
    document.addEventListener('shopify:section:unload', unload);
    document.addEventListener('realm:product-discovery-updated', refresh);
    window.addEventListener('resize', refresh);
    window.addEventListener('pageshow', refresh);
    register();
    return { register, inspect, entries, destroy() { unregister(); document.removeEventListener('shopify:section:load', load); document.removeEventListener('shopify:section:unload', unload); document.removeEventListener('realm:product-discovery-updated', refresh); window.removeEventListener('resize', refresh); window.removeEventListener('pageshow', refresh); } };
  }
  return { scrollState, authoredUnit, sizeToken, productSizes, filterTableSizes, filterGuideSizes, preparePage, mount };
});
